import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getListPath, isRoutableListId } from '../utils/listUrl';

/**
 * The list shown by default at the homepage (/) for anonymous visitors with an
 * empty watchlist. Single source of truth, shared with App.js (initial
 * selectedTab / empty-watchlist fallback) and ListSEO.
 *
 * SEO INVARIANT: this tab must stay at the root URL (/) on first load and must
 * NOT be redirected to /list/<id>. If it is, Googlebot crawls / but the rendered
 * canonical/title/description end up pointing at the list page, so the homepage
 * gets indexed with the list's title (e.g. "In The News | QuantApe...") instead
 * of the homepage title. ListSEO keys homepage meta off pathname === '/', and the
 * initial-mount redirect below skips this tab. Keep all three in sync if changed.
 */
export const HOMEPAGE_DEFAULT_TAB = 'in_the_news';

/**
 * ListRouteSync - Synchronizes URL with selected list/tab
 *
 * URL formats:
 *   /list/{listId}           - Smart lists, user lists
 *   /portfolio/{portfolioId} - AI portfolios
 *   /list/strategy_{id}      - Strategy tabs (issue #337)
 *
 * This component:
 * 1. On mount/URL change: Reads ID from URL and calls setSelectedTab
 * 2. On selectedTab change: Updates URL to match
 *
 * Special cases:
 * - 'watchlist' stays at root (/) to avoid ugly /list/watchlist URL
 * - HOMEPAGE_DEFAULT_TAB stays at root (/) on first load for SEO (see above)
 * - Portfolio tabs (ptf_*) use /portfolio/ prefix
 * - Unknown lists are handled gracefully (no navigation)
 */
const ListRouteSync = ({ selectedTab, setSelectedTab, categories, userListsData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const lastSyncedTab = useRef(null);

  // Extract ID from URL path (supports both /list/ and /portfolio/)
  const getIdFromPath = (pathname) => {
    const listMatch = pathname.match(/^\/list\/(.+)$/);
    if (listMatch) return listMatch[1];
    const portfolioMatch = pathname.match(/^\/portfolio\/(.+)$/);
    if (portfolioMatch) return portfolioMatch[1];
    return null;
  };

  // Check if a listId is valid (exists in categories or userLists)
  const isValidListId = (listId) => {
    if (!listId) return false;
    if (listId === 'watchlist') return true;

    // AI portfolios, strategy tabs, smart lists and user lists (pure, in utils/listUrl.js).
    return isRoutableListId(listId, categories, userListsData);
  };

  // URL -> selectedTab: When URL changes, update selectedTab
  useEffect(() => {
    const id = getIdFromPath(location.pathname);

    if (id && id !== selectedTab) {
      // Only set if it's a valid list
      if (isValidListId(id)) {
        console.log('[LIST-ROUTE] URL -> Tab:', id);
        lastSyncedTab.current = id;
        setSelectedTab(id);
      } else if (categories) {
        // Categories loaded but list not found - could be invalid URL
        console.log('[LIST-ROUTE] Invalid list in URL:', id);
      }
      // If categories not loaded yet, we'll check again when they load
    }
  }, [location.pathname, categories, userListsData, setSelectedTab]);

  // selectedTab -> URL: When tab changes, update URL
  useEffect(() => {
    // Skip initial mount to avoid double-setting on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // But if we have a tab selected and we're at root, update URL.
      // Exception: the homepage default tab (like watchlist) stays at / so the
      // homepage keeps its own canonical/title for crawlers. See SEO INVARIANT.
      if (
        selectedTab &&
        selectedTab !== 'watchlist' &&
        selectedTab !== HOMEPAGE_DEFAULT_TAB &&
        location.pathname === '/'
      ) {
        navigate(getListPath(selectedTab), { replace: true });
        lastSyncedTab.current = selectedTab;
      }
      return;
    }

    // Skip if this change came from URL sync (prevent loop)
    if (lastSyncedTab.current === selectedTab) {
      lastSyncedTab.current = null;
      return;
    }

    const currentId = getIdFromPath(location.pathname);

    // Update URL if tab changed and URL doesn't match
    if (selectedTab !== currentId) {
      if (selectedTab === 'watchlist') {
        // Watchlist goes to root
        if (location.pathname !== '/') {
          console.log('[LIST-ROUTE] Tab -> URL: / (watchlist)');
          navigate('/', { replace: true });
        }
      } else if (selectedTab) {
        const path = getListPath(selectedTab);
        console.log('[LIST-ROUTE] Tab -> URL:', path);
        navigate(path, { replace: true });
      }
    }
  }, [selectedTab, location.pathname, navigate]);

  // This component doesn't render anything
  return null;
};

export default ListRouteSync;
